/**
 * Foundry-curated suggestion catalogs for the Concept Studio.
 *
 * Deliberately framed for Foundry's actual game space — TV + phone-controller
 * party / social / trivia multiplayer — NOT the single-player-console vocabulary
 * of the original prototype (which drifted toward "explore/survive/combat"). The
 * write-in field is always available, so these are starting points, not a
 * closed set. Keeping the vocabulary on-platform is what lets the generated
 * pitch stay specific and scoped instead of generic.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Brain,
  Laugh,
  Users,
  Timer,
  Sparkles,
  Clover,
  TrendingUp,
  Eye,
  HelpCircle,
  Drama,
  Pencil,
  ThumbsUp,
  Zap,
  Type,
  GitFork,
  PartyPopper,
  Coins,
  Swords,
  Coffee,
  Shuffle,
  Heart,
  Gamepad2,
  Ghost,
  Lightbulb,
  Trophy,
  Puzzle,
  Music,
  Map,
  Dice5,
  Sword,
  Flag,
} from 'lucide-react';

export interface CatalogEntry {
  /** The value stored in selection state AND shown on the chip. */
  label: string;
  icon: LucideIcon;
}

/** "What do you want players to feel?" — multi-select. */
export const FEELS_CATALOG: CatalogEntry[] = [
  { label: 'Outsmart everyone', icon: Brain },
  { label: 'Laugh together', icon: Laugh },
  { label: 'Team up', icon: Users },
  { label: 'Beat the buzzer', icon: Timer },
  { label: 'Show off', icon: Sparkles },
  { label: 'Get lucky', icon: Clover },
  { label: 'Pull off an upset', icon: TrendingUp },
  { label: 'Read the room', icon: Eye },
];

/** "What will players do?" — multi-select. */
export const ACTIVITIES_CATALOG: CatalogEntry[] = [
  { label: 'Answer trivia', icon: HelpCircle },
  { label: 'Guess & bluff', icon: Drama },
  { label: 'Draw & doodle', icon: Pencil },
  { label: 'Vote & rank', icon: ThumbsUp },
  { label: 'Tap fast', icon: Zap },
  { label: 'Play with words', icon: Type },
  { label: 'Pick sides', icon: GitFork },
  { label: 'Act it out', icon: PartyPopper },
  { label: 'Bid & risk', icon: Coins },
];

/** "Genres to mix" — multi-select. A few familiar reference points to blend;
 *  the write-in covers anything else. */
export const GENRES_CATALOG: CatalogEntry[] = [
  { label: 'Trivia', icon: HelpCircle },
  { label: 'Party', icon: PartyPopper },
  { label: 'Puzzle', icon: Puzzle },
  { label: 'Word', icon: Type },
  { label: 'Racing', icon: Flag },
  { label: 'Rhythm', icon: Music },
  { label: 'Roguelike', icon: Map },
  { label: 'Fighting', icon: Sword },
  { label: 'Auction', icon: Coins },
  { label: 'Sports', icon: Trophy },
  { label: 'Casino', icon: Dice5 },
];

/** "Set the mood" — multi-select. */
export const MOOD_CATALOG: CatalogEntry[] = [
  { label: 'Cutthroat', icon: Swords },
  { label: 'Chill', icon: Coffee },
  { label: 'Chaotic', icon: Shuffle },
  { label: 'Wholesome', icon: Heart },
  { label: 'Retro', icon: Gamepad2 },
  { label: 'Slick', icon: Sparkles },
  { label: 'Spooky', icon: Ghost },
  { label: 'Big-brain', icon: Lightbulb },
];

/** Sensible non-empty defaults so the studio shows a materialised concept
 *  the instant it opens (mirrors the prototype's pre-selected state). Genres
 *  default to empty — genre-mixing is opt-in. */
export const DEFAULT_FEELS = ['Outsmart everyone', 'Laugh together'];
export const DEFAULT_ACTIVITIES = ['Answer trivia', 'Guess & bluff'];
export const DEFAULT_GENRES: string[] = [];
export const DEFAULT_MOODS = ['Chaotic'];
export const DEFAULT_PACE = 35;
